import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { SuperAdminLayout } from './SuperAdminLayout';
import {
  BarChart3, Wrench, Users, DollarSign, Clock, TrendingUp,
  Building2, Package, Activity, ArrowUpRight
} from 'lucide-react';

const SuperAdminAnalytics = () => {
  const { api } = useAuth();
  const { t, formatCurrency } = useLanguage();
  const [overview, setOverview] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [ov, comp] = await Promise.all([
          api.get('/super-admin/analytics/overview'),
          api.get('/super-admin/analytics/companies'),
        ]);
        setOverview(ov.data);
        setCompanies(comp.data.companies || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, [api]);

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#009b3a] border-t-transparent rounded-full animate-spin" />
        </div>
      </SuperAdminLayout>
    );
  }

  const ov = overview || {};
  const PLAN_COLORS = { start: '#3b82f6', pro: '#fedd00', super: '#00d946' };

  return (
    <SuperAdminLayout>
      <div className="space-y-6" data-testid="super-admin-analytics">
        <div>
          <h1 className="text-xl font-bold text-white">{t('saAnalytics.title')}</h1>
          <p className="text-sm text-gray-500">{t('saAnalytics.subtitle')}</p>
        </div>

        {/* Global KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { icon: Wrench, label: t('saAnalytics.kpiRepairsToday'), value: ov.repairs_today || 0, color: 'bg-[#009b3a]/15 text-[#00d946]' },
            { icon: Activity, label: t('saAnalytics.kpiActiveRepairs'), value: ov.active_repairs || 0, color: 'bg-blue-500/15 text-blue-400' },
            { icon: DollarSign, label: t('saAnalytics.kpiRevenueMonth'), value: formatCurrency(ov.revenue_month || 0, 'EUR', { maximumFractionDigits: 0 }), color: 'bg-emerald-500/15 text-emerald-400' },
            { icon: Users, label: t('saAnalytics.kpiClientsTotal'), value: ov.clients_total || 0, color: 'bg-amber-500/15 text-amber-400' },
          ].map((s, i) => (
            <div key={i} className="bg-[#111827] border border-white/5 rounded-xl p-4" data-testid={`kpi-${i}`}>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${s.color}`}>
                <s.icon className="w-4 h-4" />
              </div>
              <p className="text-xl font-bold text-white">{s.value}</p>
              <p className="text-[11px] text-gray-500 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Secondary KPIs */}
        <div className="grid grid-cols-3 lg:grid-cols-5 gap-3">
          {[
            { label: t('saAnalytics.kpiRepairsWeek'), value: ov.repairs_week || 0 },
            { label: t('saAnalytics.kpiRepairsMonth'), value: ov.repairs_month || 0 },
            { label: t('saAnalytics.kpiRepairsTotal'), value: ov.repairs_total || 0 },
            { label: t('saAnalytics.kpiRevenueTotal'), value: formatCurrency(ov.revenue_total || 0, 'EUR', { maximumFractionDigits: 0 }) },
            { label: t('saAnalytics.kpiActiveCompanies'), value: ov.total_companies || 0 },
          ].map((s, i) => (
            <div key={i} className="bg-[#111827] border border-white/5 rounded-xl p-3">
              <p className="text-lg font-bold text-white">{s.value}</p>
              <p className="text-[10px] text-gray-500">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Company Performance Table */}
        <div className="bg-[#111827] border border-white/5 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#009b3a]" />
            <h2 className="text-sm font-semibold text-white">{t('saAnalytics.companyPerformance')}</h2>
            <span className="text-xs text-gray-500 ml-auto">{t('saAnalytics.companiesCount', { count: companies.length })}</span>
          </div>

          {companies.length === 0 ? (
            <p className="p-6 text-sm text-gray-500 text-center">{t('saAnalytics.emptyCompanies')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="company-analytics-table">
                <thead>
                  <tr className="text-gray-500 border-b border-white/5">
                    <th className="text-left px-4 py-3 font-medium">{t('saAnalytics.colCompany')}</th>
                    <th className="text-center px-3 py-3 font-medium">{t('saAnalytics.colPlan')}</th>
                    <th className="text-right px-3 py-3 font-medium">{t('saAnalytics.colRepairs')}</th>
                    <th className="text-right px-3 py-3 font-medium">{t('saAnalytics.colActive')}</th>
                    <th className="text-right px-3 py-3 font-medium">{t('saAnalytics.colClients')}</th>
                    <th className="text-right px-3 py-3 font-medium">{t('saAnalytics.colStock')}</th>
                    <th className="text-right px-3 py-3 font-medium">{t('saAnalytics.colSales')}</th>
                    <th className="text-right px-3 py-3 font-medium">{t('saAnalytics.colRevenue')}</th>
                    <th className="text-right px-3 py-3 font-medium">{t('saAnalytics.colAvgTime')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {companies.map(c => (
                    <tr key={c.company_id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-[#009b3a]/10 flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-3.5 h-3.5 text-[#009b3a]" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{c.name}</p>
                            {c.prefix && <p className="text-[10px] text-gray-600 font-mono">{c.prefix}xxxx</p>}
                          </div>
                        </div>
                      </td>
                      <td className="text-center px-3 py-3">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase" style={{ background: `${PLAN_COLORS[c.plan] || '#666'}20`, color: PLAN_COLORS[c.plan] || '#666' }}>
                          {c.plan}
                        </span>
                      </td>
                      <td className="text-right px-3 py-3 font-mono text-white">{c.total_repairs}</td>
                      <td className="text-right px-3 py-3">
                        <span className={c.active_repairs > 0 ? 'text-blue-400 font-semibold' : 'text-gray-600'}>{c.active_repairs}</span>
                      </td>
                      <td className="text-right px-3 py-3 text-gray-300">{c.total_clients}</td>
                      <td className="text-right px-3 py-3 text-gray-300">{c.total_stock}</td>
                      <td className="text-right px-3 py-3 text-gray-300">{c.total_sales}</td>
                      <td className="text-right px-3 py-3">
                        <span className="text-emerald-400 font-semibold">{formatCurrency(c.revenue, 'EUR', { maximumFractionDigits: 0 })}</span>
                      </td>
                      <td className="text-right px-3 py-3">
                        {c.avg_repair_days > 0 ? (
                          <span className={`flex items-center justify-end gap-1 ${c.avg_repair_days <= 3 ? 'text-emerald-400' : c.avg_repair_days <= 7 ? 'text-amber-400' : 'text-red-400'}`}>
                            <Clock className="w-3 h-3" />{t('saAnalytics.daysShort', { n: c.avg_repair_days })}
                          </span>
                        ) : <span className="text-gray-600">-</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminAnalytics;
